import * as Sentry from "@sentry/react";
import { useCallback } from "react";
import { useStore } from "react-redux";

import { useAppDispatch } from "~/app/store";
import { fetchFulfillmentCenter } from "~/redux/actions/store";
import { setSiteFcId } from "~/redux/actions/workstations";
import { StoreState } from "~/redux/reducers";
import { selectSiteFcAbbreviationTrimmed } from "~/redux/selectors/storeSelectors";
import { selectWorkstationSiteFulfillmentCenterId } from "~/redux/selectors/workstationsSelectors";
import { useLazyGetAllFulfillmentCentersQuery } from "~/redux/warehouse/fulfillmentCenter.hooks";

/**
 * Validates the persisted site FC id against the warehouse list and refreshes
 * FC details when it still exists. Does not clear the selection if the id is
 * missing from the list or the request fails (those go to Sentry).
 *
 * Returned abbreviation is for ?fc= on redirect: prefer the list, else Redux.
 * Undefined only when no site FC is selected.
 */
export function useValidateFulfillmentCenter() {
  const dispatch = useAppDispatch();
  const store = useStore<StoreState>();
  const [getAllFulfillmentCenters] = useLazyGetAllFulfillmentCentersQuery();

  const validateFc = useCallback(async (): Promise<string | undefined> => {
    const siteFulfillmentCenterId = selectWorkstationSiteFulfillmentCenterId(
      store.getState()
    );

    const persistedAbbreviation = () =>
      selectSiteFcAbbreviationTrimmed(store.getState());

    if (!siteFulfillmentCenterId) {
      return undefined;
    }

    try {
      const clientFcs = await getAllFulfillmentCenters().unwrap();

      const matchedFc = clientFcs.find(
        (fc) => fc.fulfillmentCenterId === siteFulfillmentCenterId
      );

      if (!matchedFc) {
        Sentry.captureMessage(
          "Persisted site fulfillment center id not found in warehouse list",
          {
            level: "warning",
            tags: { feature: "fulfillment-center-sync" },
            extra: {
              siteFulfillmentCenterId,
              warehouseListLength: clientFcs.length
            }
          }
        );
        return persistedAbbreviation();
      }

      if (matchedFc.abbreviation != null) {
        const serverAbbr = matchedFc.abbreviation.trim();
        const persistedAbbr = persistedAbbreviation() ?? "";

        if (serverAbbr !== persistedAbbr) {
          dispatch(
            setSiteFcId(siteFulfillmentCenterId, matchedFc.abbreviation)
          );
        }
      }

      await dispatch(fetchFulfillmentCenter(siteFulfillmentCenterId));
      const fromServer = matchedFc.abbreviation?.trim();
      return fromServer || persistedAbbreviation();
    } catch (err) {
      Sentry.captureException(err, {
        tags: { feature: "fulfillment-center-sync" }
      });
      return persistedAbbreviation();
    }
  }, [dispatch, getAllFulfillmentCenters, store]);

  return { validateFc };
}
